// this component displays the order history for a logged-in user, including past orders and receipts. It fetches the user's paid carts and their associated transactions, grouping them by cart ID. Users can expand each order to view its items and download receipts. The component also handles pagination and highlights specific orders based on deep links.
import React, { useState, useEffect, useRef } from 'react';
import { useAuth, supabase } from '../context/AuthContext';
import { convertToMelbourneDateAndTime } from '../utils/dateUtils';
import useHighlightOrderId from '../hooks/useHighlightOrderId';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import { InformationCircleIcon } from '@heroicons/react/24/solid';
import CustomerLayout from './CustomerLayout';

const OrderHistory = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [downloadingCartId, setDownloadingCartId] = useState(null);
  const [highlightedCartId, setHighlightedCartId] = useState(null);
  const orderRefs = useRef({});

  const [currentPage, setCurrentPage] = useState(1);
  const [ordersPerPage] = useState(50);
  const highlightOrderId = useHighlightOrderId('/order-history');

  useEffect(() => {
    if (user) {
      fetchOrders();
    }
  }, [user]);

  const indexOfLastOrder = currentPage * ordersPerPage;
  const indexOfFirstOrder = indexOfLastOrder - ordersPerPage;
  const currentOrders = orders.slice(indexOfFirstOrder, indexOfLastOrder);
  const totalPages = Math.ceil(orders.length / ordersPerPage);

  // If a highlightOrderId is present, find the corresponding order and expand it. If the order is on a different page, switch to that page.
  useEffect(() => {
    if (!highlightOrderId || orders.length === 0) {
      return;
    }

    const targetIndex = orders.findIndex((orderGroup) => {
      if (String(orderGroup.cartId) === String(highlightOrderId)) {
        return true;
      }

      return orderGroup.items.some((item) => String(item.orderId) === String(highlightOrderId));
    });

    if (targetIndex === -1) {
      return;
    }

    const targetOrder = orders[targetIndex];
    const targetPage = Math.floor(targetIndex / ordersPerPage) + 1;

    setCurrentPage((prevPage) => (prevPage === targetPage ? prevPage : targetPage));
    setExpandedRows((prev) => {
      const next = new Set(prev);
      next.add(targetOrder.cartId);
      return next;
    });
    setHighlightedCartId(targetOrder.cartId);
  }, [highlightOrderId, orders, ordersPerPage]);

  // Scrolls the highlighted row into view once it is rendered, then fades the highlight
  // off after 3s. Re-runs on page change because the row mounts only on its own page.
  useEffect(() => {
    if (!highlightedCartId) {
      return;
    }

    const targetElement = orderRefs.current[highlightedCartId];
    if (!targetElement) {
      return;
    }

    targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });

    const timeoutId = window.setTimeout(() => {
      setHighlightedCartId((current) => (current === highlightedCartId ? null : current));
    }, 3000);

    return () => window.clearTimeout(timeoutId);
  }, [highlightedCartId, currentPage]);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Receipts live under `<user id>/receipts/` in the private `users` bucket
  const listReceipts = async () => {
    try {
      const { data, error } = await supabase.storage
        .from('users')
        .list(`${user.id}/receipts/`);

      if (error) {
        console.error('Error checking storage:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error checking receipt existence:', error);
      return [];
    }
  };

  const parseStoredIds = (value) => {
    if (!value) {
      return [];
    }

    if (Array.isArray(value)) {
      return value.filter(Boolean).map(String).map((item) => item.trim()).filter(Boolean);
    }

    if (typeof value === 'string') {
      const trimmedValue = value.trim();

      if (!trimmedValue) {
        return [];
      }

      try {
        const parsedValue = JSON.parse(trimmedValue);
        if (Array.isArray(parsedValue)) {
          return parsedValue.filter(Boolean).map(String).map((item) => item.trim()).filter(Boolean);
        }
      } catch {
        const cleanString = trimmedValue.replace(/^\[|\]$/g, '');
        return cleanString
          .split(',')
          .map(item => item.trim().replace(/^['"]|['"]$/g, ''))
          .filter(Boolean);
      }
    }

    return [];
  };

  const fetchOrders = async () => {
    try {
      setLoading(true);

      const { data: paidCarts, error: cartError } = await supabase
        .from('cart')
        .select(`
          *,
          created_at
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (cartError) {
        console.error('Cart Error:', cartError);
        throw cartError;
      }

      if (!paidCarts || paidCarts.length === 0) {
        setOrders([]);
        setLoading(false);
        return;
      }

      const transactionToCartMap = {};
      const allTransactionIds = paidCarts.reduce((acc, cart) => {
        if (cart.transaction_ids) {
          try {
            const parsedTransactionIds = parseStoredIds(cart.transaction_ids);

            parsedTransactionIds.forEach(transactionId => {
              if (transactionId) {
                transactionToCartMap[transactionId] = {
                  created_at: cart.created_at,
                  cart_id: cart.id,
                  total_amount: cart.total_amount
                };
              }
            });
            return acc.concat(parsedTransactionIds);
          } catch (e) {
            console.error('Error parsing transaction ids for cart', cart.id, ':', e);
            return acc;
          }
        }
        return acc;
      }, []);

      let transactions = [];
      if (allTransactionIds.length > 0) {
        const { data: transactionData, error: transactionError } = await supabase
          .from('transactions')
          .select(`
            id,
            price, 
            invoice_number, 
            submission_id,
            created_at,
            modelreports(
              id,
              vin_number, 
              customer_unique_id, 
              customer_model_reports(
                make, 
                model, 
                mr_approval
              )
            )
          `)
          .in('id', allTransactionIds);

        if (transactionError) {
          console.error('Transaction Error:', transactionError);
          throw transactionError;
        }

        transactions = transactionData || [];
      }

      const groupedOrders = {};

      if (transactions.length > 0) {
        transactions.forEach(transaction => {
          if (!transaction.modelreports || !transaction.modelreports.customer_model_reports) {
            return;
          }

          const cartInfo = transactionToCartMap[transaction.id];
          if (!cartInfo) {
            return;
          }

          const cartId = cartInfo.cart_id;

          if (!groupedOrders[cartId]) {
            groupedOrders[cartId] = {
              date: cartInfo.created_at,
              cartId: cartId,
              items: [],
              totalAmount: cartInfo.total_amount || 0
            };
          }

          const modelReport = transaction.modelreports.customer_model_reports;

          groupedOrders[cartId].items.push({
            orderId: transaction.modelreports.id,
            name: `${modelReport.make} ${modelReport.model}`,
            uniqueId: transaction.modelreports.customer_unique_id,
            vin: transaction.modelreports.vin_number,
            mreNumber: modelReport.mr_approval,
            price: transaction.price || 0,
            invoiceNumber: transaction.invoice_number,
            cartDate: cartInfo.created_at
          });
        });
      }

      const receiptFiles = await listReceipts();
      // Receipt filenames are not matched to a cart id — this only asks whether the
      // customer has any receipt stored at all
      const receiptExistsForCart = () => receiptFiles.length > 0;

      // Older carts whose transaction ids no longer resolve still get a receipt-only row
      for (const cart of paidCarts) {
        if (!groupedOrders[cart.id]) {
          const receiptExists = receiptExistsForCart();
          if (receiptExists) {
            groupedOrders[cart.id] = {
              date: cart.created_at,
              cartId: cart.id,
              items: [],
              totalAmount: cart.total_amount || 0,
              hasReceipt: true,
              noTransactionDetails: true
            };
          }
        }
      }

      // Receipt-only carts are appended out of order above, so sort newest-first at the end
      const formattedOrders = Object.values(groupedOrders).sort((a, b) =>
        new Date(b.date).getTime() - new Date(a.date).getTime()
      );

      setOrders(formattedOrders);
      setCurrentPage(1);
    } catch (error) {
      console.error('Error in fetchOrders:', error);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleRow = (cartId) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(cartId)) {
        newSet.delete(cartId);
      } else {
        newSet.add(cartId);
      }
      return newSet;
    });
  };

  // Receipts are keyed by cart id, but two naming conventions exist in storage, so try both
  const downloadReceipt = async (orderGroup) => {
    try {
      const cartId = orderGroup.cartId;
      setDownloadingCartId(cartId);
      const receiptCandidates = [
        `${user.id}/receipts/${cartId}.pdf`,
        `${user.id}/receipts/${cartId}-REC.pdf`,
      ];

      let data = null;
      let lastError = null;

      for (const filePath of receiptCandidates) {
        const result = await supabase.storage
          .from('users')
          .download(filePath);

        if (result.data) {
          data = result.data;
          break;
        }

        lastError = result.error;
      }

      if (!data) {
        console.error('Error downloading receipt:', lastError);
        throw lastError || new Error('Receipt not found');
      }

      const blob = new Blob([data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `receipt_${cartId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading receipt:', error);
      alert('Failed to download receipt. Please try again later.');
    } finally {
      setDownloadingCartId(null);
    }
  };

  // Sliding window of at most 5 page buttons, kept centred on the current page
  const PaginationControls = () => {
    const maxVisiblePages = 5;
    const pageNumbers = [];

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    if (totalPages <= 1) return null;

    return (
      <div className="flex items-center justify-between border-t border-slate-200 bg-white px-5 py-3 sm:px-6">
        <p className="text-sm text-slate-700">
          Showing <span className="font-medium">{indexOfFirstOrder + 1}</span> to{' '}
          <span className="font-medium">{Math.min(indexOfLastOrder, orders.length)}</span> of{' '}
          <span className="font-medium">{orders.length}</span> orders
        </p>
        <nav className="inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
          <button
            type="button"
            onClick={() => handlePageChange(Math.max(currentPage - 1, 1))}
            disabled={currentPage === 1}
            className="relative inline-flex items-center rounded-l-[0.75rem] border border-slate-300 bg-white px-2 py-2 text-slate-400 hover:bg-slate-50 disabled:opacity-50"
          >
            <span className="sr-only">Previous</span>
            <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
          </button>

          {startPage > 1 && (
            <>
              <button
                type="button"
                onClick={() => handlePageChange(1)}
                className="relative inline-flex items-center border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                1
              </button>
              {startPage > 2 && (
                <span className="relative inline-flex items-center border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700">
                  ...
                </span>
              )}
            </>
          )}

          {pageNumbers.map((number) => (
            <button
              key={number}
              type="button"
              onClick={() => handlePageChange(number)}
              className={`relative inline-flex items-center border px-4 py-2 text-sm font-medium ${
                currentPage === number
                  ? 'z-10 border-blue-500 bg-blue-50 text-blue-600'
                  : 'border-slate-300 text-slate-700 hover:bg-slate-50'
              }`}
            >
              {number}
            </button>
          ))}

          {endPage < totalPages && (
            <>
              {endPage < totalPages - 1 && (
                <span className="relative inline-flex items-center border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700">
                  ...
                </span>
              )}
              <button
                type="button"
                onClick={() => handlePageChange(totalPages)}
                className="relative inline-flex items-center border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                {totalPages}
              </button>
            </>
          )}

          <button
            type="button"
            onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="relative inline-flex items-center rounded-r-[0.75rem] border border-slate-300 bg-white px-2 py-2 text-slate-400 hover:bg-slate-50 disabled:opacity-50"
          >
            <span className="sr-only">Next</span>
            <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
          </button>
        </nav>
      </div>
    );
  };

  const OrderItemCard = ({ item, index }) => (
    <div className="rounded-[1rem] border border-slate-200 bg-slate-50/80 px-4 py-3 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-blue-600">
            <span className="mr-2 text-slate-400">{index + 1}.</span>
            {item.name}
          </p>
          <div className="mt-2 space-y-1">
            <p className="text-sm">
              <span className="text-slate-500">Unique ID:</span>{' '}
              <span className="text-slate-700">{item.uniqueId}</span>
            </p>
            <p className="text-sm">
              <span className="text-slate-500">VIN:</span>{' '}
              <span className="text-slate-700">{item.vin}</span>
            </p>
            <p className="text-sm">
              <span className="text-slate-500">MRE Approval:</span>{' '}
              <span className="text-slate-700">{item.mreNumber}</span>
            </p>
            <p className="text-sm">
              <span className="text-slate-500">Invoice Number:</span>{' '}
              <span className="text-slate-700">{item.invoiceNumber}</span>
            </p>
          </div>
        </div>
        <p className="shrink-0 text-sm font-semibold text-blue-600">
          ${Number(item.price).toFixed(2)}
        </p>
      </div>
    </div>
  );

  if (loading) {
    return (
      <CustomerLayout
        activeItemId="order-history"
        title="Order History"
        subtitle="View past orders and download receipts."
        contentClassName="mx-auto w-full max-w-none"
      >
        <div className="flex items-center justify-center py-24">
          <div className="text-center">
            <div className="mx-auto h-8 w-8 animate-spin rounded-full border-b-2 border-blue-500" />
            <p className="mt-2 text-sm text-slate-500">Loading orders...</p>
          </div>
        </div>
      </CustomerLayout>
    );
  }

  return (
    <CustomerLayout
      activeItemId="order-history"
      title="Order History"
      subtitle="View past orders and download receipts."
      contentClassName="mx-auto w-full max-w-none"
    >
      <div className="mb-6 overflow-hidden rounded-[2rem] border border-blue-200 bg-blue-50/80 shadow-sm">
        <div className="flex items-start gap-3 p-4 sm:p-5">
          <InformationCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-blue-600" />
          <div>
            <h3 className="text-sm font-semibold text-blue-600 uppercase tracking-[0.04em]">Receipt Information</h3>
            <p className="mt-1 text-sm leading-6 text-blue-600">
              For orders placed before May 2025, receipts were sent directly to your email address.
              If you need a receipt from an older order, please check your email or contact our support team.
            </p>
          </div>
        </div>
      </div>

      <div className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 px-6 py-4">
          <h2 className="text-md font-medium text-slate-900 uppercase tracking-[0.04em]">Past Orders</h2>
        </div>

        {orders.length === 0 ? (
          <div className="px-6 py-16 text-center">
            <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-slate-100">
              <svg className="h-10 w-10 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="mt-4 text-lg font-medium text-slate-900">No Orders Found</h3>
            <p className="mt-2 text-sm text-slate-500">
              Your completed orders and receipts will appear here once you make a purchase.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full table-auto divide-y divide-slate-200 bg-white">
              <thead className="bg-slate-50">
                <tr>
                  <th className="w-12 px-4 py-4 text-left text-xs font-medium uppercase tracking-wider text-slate-500" />
                  <th className="px-4 py-4 text-left text-xs font-medium uppercase tracking-wider text-slate-500">
                    Order Date
                  </th>
                  <th className="hidden px-4 py-4 text-left text-xs font-medium uppercase tracking-wider text-slate-500 sm:table-cell">
                    Items
                  </th>
                  <th className="px-4 py-4 text-left text-xs font-medium uppercase tracking-wider text-slate-500">
                    Total Amount
                  </th>
                  <th className="px-4 py-4 text-center text-xs font-medium uppercase tracking-wider text-slate-500">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 bg-white">
                {currentOrders.map((orderGroup) => {
                  const isExpanded = expandedRows.has(orderGroup.cartId);
                  const isHighlighted = highlightedCartId === orderGroup.cartId;

                  return (
                    <React.Fragment key={orderGroup.cartId}>
                      <tr
                        ref={(element) => {
                          // Store a reference to the row element for scrolling and highlighting purposes. If the element is unmounted, remove it from the refs.
                          if (element) {
                            orderRefs.current[orderGroup.cartId] = element;
                            return;
                          }
                          delete orderRefs.current[orderGroup.cartId];
                        }}
                        className={`cursor-pointer transition-colors hover:bg-slate-50 ${
                          isHighlighted ? 'bg-blue-50 ring-2 ring-inset ring-blue-400' : ''
                        }`}
                        onClick={() => toggleRow(orderGroup.cartId)}
                      >
                        <td className="whitespace-nowrap px-4 py-4">
                          <button
                            type="button"
                            className="text-slate-500 hover:text-slate-700 focus:outline-none"
                            tabIndex={-1}
                          >
                            {isExpanded ? (
                              <ChevronUpIcon className="h-5 w-5" />
                            ) : (
                              <ChevronDownIcon className="h-5 w-5" />
                            )}
                          </button>
                        </td>
                        <td className="max-w-[8.5rem] whitespace-normal break-words px-4 py-4 text-sm text-slate-700 sm:max-w-none sm:whitespace-nowrap">
                          {convertToMelbourneDateAndTime(orderGroup.date)}
                        </td>
                        <td className="hidden whitespace-nowrap px-4 py-4 text-sm text-slate-700 sm:table-cell">
                          {orderGroup.items.length > 0
                            ? `${orderGroup.items.length} item${orderGroup.items.length !== 1 ? 's' : ''}`
                            : 'Receipt only'}
                        </td>
                        <td className="whitespace-nowrap px-4 py-4 text-sm font-medium text-blue-600">
                          ${orderGroup.totalAmount.toFixed(2)}
                        </td>
                        <td className="whitespace-nowrap px-4 py-4 text-center">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              downloadReceipt(orderGroup);
                            }}
                            disabled={downloadingCartId === orderGroup.cartId}
                            title="DOWNLOAD RECEIPT"
                            aria-label="Download Receipt"
                            className="inline-flex items-center justify-center gap-1.5 rounded-[0.75rem] border border-blue-200 bg-white p-2 text-xs font-medium text-blue-600 transition hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-50 md:px-3 md:py-1.5 sm:text-sm"
                          >
                            {downloadingCartId === orderGroup.cartId ? (
                              <>
                                <span className="h-4 w-4 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
                                <span className="hidden md:inline">Downloading...</span>
                              </>
                            ) : (
                              <>
                                <ArrowDownTrayIcon className="h-4 w-4" />
                                <span className="hidden md:inline">Download Receipt</span>
                              </>
                            )}
                          </button>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="bg-slate-50/70">
                          <td colSpan="5" className="px-5 py-5 lg:px-6">
                            {orderGroup.items.length > 0 ? (
                              <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_18px_40px_-28px_rgba(15,23,42,0.35)]">
                                <div className="border-b border-slate-100 bg-slate-50/80 px-4 py-3">
                                  <h3 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.08em] text-blue-600">
                                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                    Order Items
                                  </h3>
                                </div>
                                <div className="space-y-3 p-4 lg:p-5">
                                  {orderGroup.items.map((item, index) => (
                                    <OrderItemCard key={index} item={item} index={index} />
                                  ))}
                                  <div className="flex items-center justify-between border-t border-slate-200 pt-4">
                                    <span className="text-sm font-medium text-slate-900">Total Amount</span>
                                    <span className="text-lg font-bold text-blue-600">
                                      ${orderGroup.totalAmount.toFixed(2)}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <div className="rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50 px-6 py-8 text-center">
                                <p className="text-sm font-medium text-slate-700">
                                  Receipt available for download
                                </p>
                                <p className="mt-1 text-xs text-slate-500">
                                  {orderGroup.noTransactionDetails
                                    ? 'Check your email for the receipt or click the download button above.'
                                    : 'No item details found for this order.'}
                                </p>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
            <PaginationControls />
          </div>
        )}
      </div>
    </CustomerLayout>
  );
};

export default OrderHistory;
